import { Currency } from 'domains-billing-currency';
import { currencyToGraphQL } from 'domains-billing-web-mappers';
import {
  CreateChargeRequest as GeneratedCreateChargeRequest,
  GroupType,
  InvoiceType,
} from 'graphql-query-contracts';
import {
  getProductItemsTotal,
  productItemsToQueryFormat,
} from 'lane-shared/domains/billingPayments/helpers';
import {
  Charge,
  ExternalActorType,
  ExternalEntityType,
  ExternalPayerType,
} from 'lane-shared/domains/billingPayments/types';
import { SupportedLocaleEnum } from 'localization';
import { convertToUUID } from 'uuid-encoding';

export type CreateChargeRequestParams = {
  draft: Pick<Charge, 'items'>;
  chargeName: string;
  chargeDescription?: string;
  externalPayerId: string;
  externalPayerType: ExternalPayerType;
  externalEntityId: string;
  externalEntityType: ExternalEntityType;
  channelId: string;
  actingUserId: string;
  currency: Currency;
  locale: SupportedLocaleEnum;
  invoiceType: InvoiceType;
  invoiceDueDate: Date;
  metadata: Record<string, unknown>;
  isAdminFeeFeatureEnabled: boolean;
};

type ContractFieldsWithMatchingTypes = Omit<
  GeneratedCreateChargeRequest,
  'externalPayerType' | 'externalEntityType' | 'externalActorType' | 'items'
>;

export function createChargeRequestFromDraft(
  params: CreateChargeRequestParams
) {
  const { draft, currency, locale, isAdminFeeFeatureEnabled } = params;

  return {
    name: params.chargeName,
    description: params.chargeDescription ?? '',
    amount: getProductItemsTotal(
      draft.items,
      locale,
      currency,
      isAdminFeeFeatureEnabled
    ),
    currency: currencyToGraphQL(currency),
    externalPayerId: convertToUUID(params.externalPayerId),
    externalPayerType: params.externalPayerType,
    groupId: convertToUUID(params.channelId),
    groupType: GroupType.GroupTypeActivateChannel,
    externalEntityId: params.externalEntityId,
    externalEntityType: params.externalEntityType,
    externalActorId: convertToUUID(params.actingUserId),
    externalActorType: ExternalActorType.EXTERNAL_ACTOR_TYPE_ACTIVATE_USER,
    metadata: JSON.stringify(params.metadata),
    invoiceType: params.invoiceType,
    invoiceDueDate: params.invoiceDueDate,
    items: productItemsToQueryFormat(draft.items, isAdminFeeFeatureEnabled),
    isDraft: false,
  } satisfies ContractFieldsWithMatchingTypes & Record<string, unknown>;
}

export type ChargeRequestFromDraft = ReturnType<
  typeof createChargeRequestFromDraft
>;
